// Case Buddy addendum builder helpers.
// This module ports the Tool18E / desktop addendum build behavior into the web case workspace.
// It only reads reviewed addendum recipient rows and address-legend metadata; Main Data is not modified.

import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { normalizeCaseAddressLegendAddressKey } from './CaseBuddyAddressLegend.js';

export const CASE_ADDENDUM_BUILD_REVIEW_ID = 'addendum_build';
export const CASE_ADDENDUM_DELIVERABLE_ID_STRUCTURED = 'addendum';
export const CASE_ADDENDUM_DELIVERABLE_ID_AUDIT = 'addendum_audit';
export const CASE_ADDENDUM_DELIVERABLE_ID_NARRATIVE = 'addendum_narrative';
export const CASE_ADDENDUM_VARIANT_STRUCTURED = 'tool18e_structured';
export const CASE_ADDENDUM_VARIANT_NARRATIVE = 'addendum_narrative';
export const CASE_ADDENDUM_RENDERER_VERSION = 'case_addendum_renderer_v2_web';

export const CASE_ADDENDUM_EXCHANGE_JURISDICTIONS = Object.freeze([
  {
    "name": "AlphaPo",
    "jurisdiction": "Republic of Panama"
  },
  {
    "name": "Arvix.pro",
    "jurisdiction": "Amsterdam, Netherlands"
  },
  {
    "name": "B2C2",
    "jurisdiction": "Cayman Islands"
  },
  {
    "name": "Biconomy",
    "jurisdiction": "Dubai"
  },
  {
    "name": "Binance",
    "jurisdiction": "Cayman Islands"
  },
  {
    "name": "Binance.US",
    "jurisdiction": "US"
  },
  {
    "name": "BingX",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Bitfinex",
    "jurisdiction": "Taipei, T'ai-pei, Taiwan and British Virgin Islands"
  },
  {
    "name": "BitFlyer",
    "jurisdiction": "Tokyo, Japan"
  },
  {
    "name": "Bitget",
    "jurisdiction": "Hong Kong"
  },
  {
    "name": "Bithumb",
    "jurisdiction": "Seoul, Republic of Korea"
  },
  {
    "name": "Bitkeep",
    "jurisdiction": "Cayman Islands"
  },
  {
    "name": "BitKub",
    "jurisdiction": "Bangkok, Thailand"
  },
  {
    "name": "BitMart",
    "jurisdiction": "Cayman Islands"
  },
  {
    "name": "Bitpay",
    "jurisdiction": "Atlanta, Georgia"
  },
  {
    "name": "Bitstamp",
    "jurisdiction": "Luxembourg City, Luxembourg and London, England"
  },
  {
    "name": "Bitstamp USA",
    "jurisdiction": "New York, New York"
  },
  {
    "name": "Bittrex",
    "jurisdiction": "Seattle, Washington"
  },
  {
    "name": "Blofin",
    "jurisdiction": "Cayman Islands, UK"
  },
  {
    "name": "BTSE",
    "jurisdiction": "British Virgin Islands"
  },
  {
    "name": "Bybit",
    "jurisdiction": "Dubai and Singapore"
  },
  {
    "name": "Caladan",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Cex.io",
    "jurisdiction": "London, England and Naperville, IL"
  },
  {
    "name": "ChainUp",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Changelly",
    "jurisdiction": "St. Vincent and the Grenadines"
  },
  {
    "name": "ChangeNow",
    "jurisdiction": "Seychelles and Saint Vincent and the Grenadines"
  },
  {
    "name": "Circle.com",
    "jurisdiction": "Boston, MA"
  },
  {
    "name": "Coinbase",
    "jurisdiction": "San Francisco, California"
  },
  {
    "name": "Coincheck",
    "jurisdiction": "Tokyo, Japan"
  },
  {
    "name": "Coindirect",
    "jurisdiction": "Isle of Man"
  },
  {
    "name": "Coinhako",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Coinone",
    "jurisdiction": "Seoul, South Korea"
  },
  {
    "name": "Coinpayments",
    "jurisdiction": "Lithuania, Seychelles and Caymen Islands"
  },
  {
    "name": "Coinspaid",
    "jurisdiction": "Estonia"
  },
  {
    "name": "CoinTiger",
    "jurisdiction": "Singapore and Virgin Islands (TOS)"
  },
  {
    "name": "CoinW",
    "jurisdiction": "Dubai"
  },
  {
    "name": "Crypto.com",
    "jurisdiction": "Singapore & US"
  },
  {
    "name": "CryptoMate",
    "jurisdiction": "Buenos Aires, Argentina and Lithuania"
  },
  {
    "name": "Cryptomus",
    "jurisdiction": "Vilnius, Lithuania and Canada"
  },
  {
    "name": "Cumberland",
    "jurisdiction": "Chicago, IL"
  },
  {
    "name": "Deribit",
    "jurisdiction": "Panama City, Panama"
  },
  {
    "name": "DigiFinex",
    "jurisdiction": "Singapore"
  },
  {
    "name": "EigenFX",
    "jurisdiction": "China"
  },
  {
    "name": "eXch",
    "jurisdiction": "Zurich, Switzerland"
  },
  {
    "name": "Exmo",
    "jurisdiction": "London, England"
  },
  {
    "name": "FalconX",
    "jurisdiction": "San Mateo, California"
  },
  {
    "name": "FTX",
    "jurisdiction": "Nassau, Bahamas"
  },
  {
    "name": "Gate.io",
    "jurisdiction": "George Town, Cayman Islands and Republic of Panama"
  },
  {
    "name": "Gemini",
    "jurisdiction": "New York, New York"
  },
  {
    "name": "HitBTC",
    "jurisdiction": "St. Vincent and the Grenadines"
  },
  {
    "name": "Hotbit",
    "jurisdiction": "Shanghai, Taipei, Hong Kong and Estonia"
  },
  {
    "name": "Hotcoin Global",
    "jurisdiction": "Sydney, Australia and England and Wales"
  },
  {
    "name": "HTX",
    "jurisdiction": "Seychelles"
  },
  {
    "name": "Huione Crypto",
    "jurisdiction": "Cambodia"
  },
  {
    "name": "HuionePay",
    "jurisdiction": "Cambodia"
  },
  {
    "name": "Hyperpay",
    "jurisdiction": "Dubai and Hong Kong"
  },
  {
    "name": "IndependentReserve",
    "jurisdiction": "Sydney, Australia and New South Wales"
  },
  {
    "name": "Indodax",
    "jurisdiction": "Bali, Indonesia"
  },
  {
    "name": "Kraken",
    "jurisdiction": "San Francisco, California"
  },
  {
    "name": "Kucoin",
    "jurisdiction": "Seychelles"
  },
  {
    "name": "Kyrrex",
    "jurisdiction": "St. Vincent and the Grenadines and US"
  },
  {
    "name": "LATOKEN",
    "jurisdiction": "St. Vincent and the Grenadines"
  },
  {
    "name": "LBank.info",
    "jurisdiction": "Indonesia and British Virgin Islands"
  },
  {
    "name": "Luno",
    "jurisdiction": "London, England"
  },
  {
    "name": "MaskEX",
    "jurisdiction": "Dubai"
  },
  {
    "name": "Maxmaicoin",
    "jurisdiction": "Taipei"
  },
  {
    "name": "MEXC",
    "jurisdiction": "Seychelles and England and Wales"
  },
  {
    "name": "Newton",
    "jurisdiction": "Toronto, Canada"
  },
  {
    "name": "Nexo",
    "jurisdiction": "Bulgaria, and Cayman Islands"
  },
  {
    "name": "OKX",
    "jurisdiction": "Seychelles and Malta"
  },
  {
    "name": "Phemex",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Poloniex",
    "jurisdiction": "Republic of Panama, Boston MA and Seychelles"
  },
  {
    "name": "ProBit",
    "jurisdiction": "Estonia"
  },
  {
    "name": "QCPCapital",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Remitano",
    "jurisdiction": "St. Vincent and the Grenadines, Seychelles, and Singapore"
  },
  {
    "name": "Revolut",
    "jurisdiction": "London, UK and New York, NY"
  },
  {
    "name": "Robinhood",
    "jurisdiction": "Menlo Park, California"
  },
  {
    "name": "Sellix",
    "jurisdiction": "Bologna, Italy"
  },
  {
    "name": "Strike",
    "jurisdiction": "El Salvador and Chicago, IL"
  },
  {
    "name": "Transak",
    "jurisdiction": "New York, NY and London, England"
  },
  {
    "name": "Trubit",
    "jurisdiction": "British Virgin Islands"
  },
  {
    "name": "UniTrustGlobal",
    "jurisdiction": "Hong Kong"
  },
  {
    "name": "Upbit",
    "jurisdiction": "Seoul, South Korea"
  },
  {
    "name": "Uphold",
    "jurisdiction": "New York, NY, and Canada"
  },
  {
    "name": "WazirX",
    "jurisdiction": "Mumbai, India"
  },
  {
    "name": "Whitebit",
    "jurisdiction": "Lithuania and London, UK"
  },
  {
    "name": "Wintermute",
    "jurisdiction": "London, UK"
  },
  {
    "name": "XBO",
    "jurisdiction": "George Town, Malta"
  },
  {
    "name": "ZB.com",
    "jurisdiction": "Zurich, Switzerland"
  },
  {
    "name": "AeroSwap.io",
    "jurisdiction": "Frankfurt, Germany"
  },
  {
    "name": "Noones.com",
    "jurisdiction": "Panama City, Panama"
  },
  {
    "name": "Noones",
    "jurisdiction": "Panama City, Panama"
  },
  {
    "name": "SwftSwap",
    "jurisdiction": "San Mateo, CA and Cayman Islands"
  },
  {
    "name": "Stake.com",
    "jurisdiction": "Sydney, Australia and Curaçao"
  },
  {
    "name": "BVNK",
    "jurisdiction": "London, England"
  },
  {
    "name": "Cobo",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Paxos",
    "jurisdiction": "New York, NY"
  },
  {
    "name": "PayPal",
    "jurisdiction": "San Jose, California and La Vista, Nebraska"
  },
  {
    "name": "500Casino",
    "jurisdiction": "Curaçao and Cyprus"
  },
  {
    "name": "ZeroHash",
    "jurisdiction": "Chicago, IL"
  },
  {
    "name": "Copper",
    "jurisdiction": "San Francisco, CA and Zug, Switzerland"
  },
  {
    "name": "Bitso",
    "jurisdiction": "Col. Polanco Reforma, México and Gibraltar"
  },
  {
    "name": "Paxful",
    "jurisdiction": "Wilmington, DE"
  },
  {
    "name": "mercuryo",
    "jurisdiction": "London, UK and Canada"
  },
  {
    "name": "Paribu",
    "jurisdiction": "Sarıyer, İstanbul"
  },
  {
    "name": "Lido",
    "jurisdiction": "Los Angeles, CA"
  },
  {
    "name": "SUN.io",
    "jurisdiction": "Singapore"
  },
  {
    "name": "Quidax",
    "jurisdiction": "San Gwann, Malta"
  },
  {
    "name": "VALR",
    "jurisdiction": "Johannesburg, South Africa"
  },
  {
    "name": "Matrixport",
    "jurisdiction": "Singapore"
  },
  {
    "name": "CashApp",
    "jurisdiction": "Delaware, USA"
  },
  {
    "name": "Cash.App",
    "jurisdiction": "Delaware, USA"
  },
  {
    "name": "N.exchange",
    "jurisdiction": "Republic of Marshall Islands"
  },
  {
    "name": "Bitvavo",
    "jurisdiction": "Amsterdam, Netherlands"
  },
  {
    "name": "Sellix.io",
    "jurisdiction": "Bologna, Italy"
  },
  {
    "name": "CoinsBuy",
    "jurisdiction": "Saint-Vincent and the Grenadines "
  },
  {
    "name": "FaucetPay.io",
    "jurisdiction": "Costa Rica"
  },
  {
    "name": "Garantex",
    "jurisdiction": "Estonia"
  },
  {
    "name": "MaiCoin",
    "jurisdiction": "Taiwan"
  },
  {
    "name": "PayKassa",
    "jurisdiction": "Seychelles"
  },
  {
    "name": "Xinbi",
    "jurisdiction": "USA"
  },
  {
    "name": "0xProcessing",
    "jurisdiction": "U.A.E."
  },
  {
    "name": "BigONE",
    "jurisdiction": "Mahé, Beau Vallon, Seychelles"
  },
  {
    "name": "UnionChain.AI",
    "jurisdiction": "Iceland"
  },
  {
    "name": "Coingate",
    "jurisdiction": "Lithuania"
  },
  {
    "name": "Cosmostation",
    "jurisdiction": "Republic of Korea"
  },
  {
    "name": "FixedFloat",
    "jurisdiction": "United Kingdom"
  },
  {
    "name": "Unocoin",
    "jurisdiction": "Bangalore, India"
  },
  {
    "name": "Triple-A",
    "jurisdiction": "Republic of Singapore"
  },
  {
    "name": "HashKey",
    "jurisdiction": "Hong Kong"
  },
  {
    "name": "AquaNow",
    "jurisdiction": "Province of British Columbia (Canada)"
  },
  {
    "name": "Roqqu",
    "jurisdiction": "Lithuania"
  },
  {
    "name": "YellowCard",
    "jurisdiction": "Republic of South Africa"
  },
  {
    "name": "PDAX.ph",
    "jurisdiction": "Republic of the Phillipines"
  },
  {
    "name": "Blockchain.com",
    "jurisdiction": "United Kingdom"
  },
  {
    "name": "ChangeHero",
    "jurisdiction": "Costa Rica"
  },
  {
    "name": "Bitrefill",
    "jurisdiction": "Stockholm, Sweden"
  },
  {
    "name": "WasabiWalletCoinjoin",
    "jurisdiction": "Gibraltar"
  },
  {
    "name": "Wasabi Wallet",
    "jurisdiction": "Gibraltar"
  }
]);

export function escapeCaseAddendumHtml(value = '') {
  return normalizeCell(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function normalizeCaseAddendumEntityKey(value = '') {
  return normalizeCell(value).toLowerCase().replace(/\s+/g, ' ');
}

export function lookupCaseAddendumJurisdiction(entityName = '') {
  const key = normalizeCaseAddendumEntityKey(entityName);
  if (!key) return 'Unknown';
  const direct = CASE_ADDENDUM_EXCHANGE_JURISDICTIONS.find(item => normalizeCaseAddendumEntityKey(item.name) === key);
  if (direct?.jurisdiction) return direct.jurisdiction;
  const loose = CASE_ADDENDUM_EXCHANGE_JURISDICTIONS.find(item => key.includes(normalizeCaseAddendumEntityKey(item.name)) || normalizeCaseAddendumEntityKey(item.name).includes(key));
  return loose?.jurisdiction || 'Unknown';
}

export function buildCaseAddendumLegendLookup(entries = []) {
  const lookup = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const address = normalizeCell(entry?.address || entry?.Address || entry?.preferredDisplayAddress || entry?.PreferredDisplayAddress);
    const label = normalizeCell(entry?.label || entry?.Label || entry?.proposedLegendLabel || entry?.ProposedLegendLabel);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (key && label && !lookup.has(key)) lookup.set(key, label);
  });
  return lookup;
}

export function resolveCaseAddendumAddressLabel(legendLookup = new Map(), address = '') {
  const key = normalizeCaseAddressLegendAddressKey(address);
  if (!key) return '';
  return normalizeCell(legendLookup.get(key));
}

export function parseCaseAddendumLooseNumber(value) {
  const text = normalizeCell(value).replace(/,/g, '');
  if (!text) return null;
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseCaseAddendumFundsFollowedValue(value = '') {
  const text = normalizeCell(value);
  if (!text) return null;
  const match = text.match(/^([\d,]+(?:\.\d+)?)\s+([A-Za-z0-9._-]+)$/);
  if (!match) return null;
  const amount = Number.parseFloat(match[1].replace(/,/g, ''));
  const token = normalizeCell(match[2]);
  if (!Number.isFinite(amount) || !token) return null;
  return { amount, token };
}

export function formatCaseAddendumNumber(amount, token = '', options = {}) {
  const safeAmount = Number(amount);
  if (!Number.isFinite(safeAmount)) return '';
  const upper = normalizeCell(token).toUpperCase();
  let min = Number.isFinite(Number(options.minimumFractionDigits)) ? Number(options.minimumFractionDigits) : 4;
  let max = Number.isFinite(Number(options.maximumFractionDigits)) ? Number(options.maximumFractionDigits) : min;
  if (upper === 'USDT' || upper === 'USDC') {
    min = 2;
    max = 2;
  } else if (upper === 'BTC' && options.kind === 'funds-followed') {
    min = 8;
    max = 8;
  }
  return safeAmount.toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });
}

export function buildCaseAddendumAmountDisplay(rawAmount = '', token = '') {
  const safeToken = normalizeCell(token);
  const parsed = parseCaseAddendumLooseNumber(rawAmount);
  if (parsed === null) {
    return safeToken ? [normalizeCell(rawAmount), safeToken].filter(Boolean).join(' ') : normalizeCell(rawAmount);
  }
  return [formatCaseAddendumNumber(parsed, safeToken, { kind: 'amount' }), safeToken].filter(Boolean).join(' ');
}

export function buildCaseAddendumFundsFollowedDisplay(rawAmount = '', token = '', rawFundsFollowed = '') {
  const safeFunds = normalizeCell(rawFundsFollowed);
  if (!safeFunds) return { display: '', note: '' };
  const parsed = parseCaseAddendumFundsFollowedValue(safeFunds);
  if (!parsed) return { display: safeFunds, note: '' };
  const txAmount = parseCaseAddendumLooseNumber(rawAmount);
  const txToken = normalizeCell(token);
  let amount = parsed.amount;
  const notes = [];
  if (txToken && parsed.token.toUpperCase() !== txToken.toUpperCase()) {
    notes.push(`Funds Followed token differs from transaction token (${parsed.token} vs ${txToken}).`);
  } else if (txAmount !== null && amount > txAmount) {
    amount = txAmount;
    notes.push('Funds Followed exceeded the transaction amount and was capped to match the transaction amount.');
  }
  return {
    display: `${formatCaseAddendumNumber(amount, parsed.token, { kind: 'funds-followed' })} ${parsed.token}`,
    note: notes.join(' ')
  };
}

export function buildCaseAddendumAggregatedFundsDisplay(tokenMap = new Map(), rawAmount = '', token = '') {
  const txToken = normalizeCell(token);
  const normalizedTxToken = txToken.toUpperCase();
  const txAmount = parseCaseAddendumLooseNumber(rawAmount);
  const mismatches = [];
  let matchingAmount = null;
  Array.from(tokenMap.keys()).sort((left, right) => left.localeCompare(right)).forEach((fundsToken) => {
    const amount = Number(tokenMap.get(fundsToken)) || 0;
    if (normalizedTxToken && fundsToken.toUpperCase() === normalizedTxToken) {
      matchingAmount = amount;
    } else {
      mismatches.push({ token: fundsToken, amount });
    }
  });
  const notes = [];
  let display = '';
  if (matchingAmount !== null) {
    let amount = matchingAmount;
    if (txAmount !== null && amount > txAmount) {
      amount = txAmount;
      notes.push('Aggregated Funds Followed exceeded the transaction amount and was capped to match the transaction amount.');
    }
    display = `${formatCaseAddendumNumber(amount, txToken, { kind: 'funds-followed' })} ${txToken}`;
  } else if (mismatches.length) {
    display = mismatches.map(item => `${formatCaseAddendumNumber(item.amount, item.token, { kind: 'funds-followed' })} ${item.token}`).join(', ');
  }
  if (mismatches.length && normalizedTxToken) {
    const mismatchText = mismatches.map(item => `${formatCaseAddendumNumber(item.amount, item.token, { kind: 'funds-followed' })} ${item.token}`).join(', ');
    notes.push(`Token mismatch observed. Transaction token is ${txToken}, but Funds Followed also includes ${mismatchText}.`);
  }
  return { display, note: notes.join(' ') };
}

export function determineCaseAddendumNetwork(address = '', token = '') {
  const safeAddress = normalizeCell(address);
  const safeToken = normalizeCell(token);
  if (safeToken.toUpperCase() === 'BTC') return 'Bitcoin';
  if (/^0x/i.test(safeAddress)) {
    if (/^BSC/i.test(safeToken)) return 'Binance Smart Chain';
    return 'Ethereum';
  }
  if (safeAddress.startsWith('T') && safeAddress.length > 1 && safeAddress[1] === safeAddress[1].toUpperCase()) return 'TRON';
  return 'Unknown';
}

export function buildCaseAddendumAddressType(legendLabel = '', notes = '') {
  const label = normalizeCell(legendLabel);
  const lowerNotes = normalizeCell(notes).toLowerCase();
  if (/hot[-\s]?wallet|service wallet|exchange wallet/i.test(label)) return 'Hot Wallet';
  if (/deposit address|deposit wallet|deposit account/i.test(label)) return 'Deposit Address';
  if (lowerNotes.includes('deposit address') && !lowerNotes.includes('likely')) return 'Deposit Address';
  return 'Likely deposit address';
}

export function normalizeCaseAddendumPathLabelForDisplay(pathLabel = '', appendLikelyMarker = false) {
  const clean = normalizeCell(pathLabel).replace(/\*+$/g, '').trim();
  return `${clean}${appendLikelyMarker ? '*' : ''}`;
}

export function buildCaseAddendumStructuredBlock(row = {}) {
  const lines = [
    `ID: ${row.displayPathLabel || '(path)'}`,
    `Date and time: ${row.dateUtc || '(blank)'}`,
    `Txn Hash #: ${row.transactionHash || '(blank)'}`,
    `From Address: ${row.fromAddress || '(blank)'}`,
    `To Address: ${row.toAddress || '(blank)'}`,
    `To Address Label: ${row.toAddressLabel || 'Likely deposit address'}`,
    `Address Type: ${row.addressType || 'Likely deposit address'}`,
    `Network: ${row.network || 'Unknown'}`,
    `Amount of Transaction: ${row.amountDisplay || '(blank)'}`
  ];
  if (row.fundsFollowedDisplay) lines.push(`Funds Followed: ${row.fundsFollowedDisplay}`);
  return lines.join('\n');
}

export function buildCaseAddendumStructuredHtmlBlock(row = {}) {
  const fundsLine = row.fundsFollowedDisplay
    ? `<br>Funds Followed: <b>${escapeCaseAddendumHtml(row.fundsFollowedDisplay)}</b>`
    : '';
  return '<div style="font-weight:none;font-family: Arial; font-size: 14.5px; color: black;">' +
    `ID: <b>${escapeCaseAddendumHtml(row.displayPathLabel || '(path)')}</b><br>` +
    `Date and time: ${escapeCaseAddendumHtml(row.dateUtc || '(blank)')}<br>` +
    `Txn Hash #: ${escapeCaseAddendumHtml(row.transactionHash || '(blank)')}<br>` +
    `From Address: <i>${escapeCaseAddendumHtml(row.fromAddress || '(blank)')}</i><br>` +
    `To Address: <font style="color:#5bcafc"><i>${escapeCaseAddendumHtml(row.toAddress || '(blank)')}</i></font><br>` +
    `To Address Label: ${escapeCaseAddendumHtml(row.toAddressLabel || 'Likely deposit address')}<br>` +
    `Address Type: ${escapeCaseAddendumHtml(row.addressType || 'Likely deposit address')}<br>` +
    `Network: ${escapeCaseAddendumHtml(row.network || 'Unknown')}<br>` +
    `Amount of Transaction: <b>${escapeCaseAddendumHtml(row.amountDisplay || '(blank)')}</b>` +
    fundsLine +
    '</div>';
}

export function buildCaseAddendumMovementText(amountDisplay = '', token = '', fundsFollowedDisplay = '') {
  if (normalizeCell(fundsFollowedDisplay)) return `The funds followed in this row were ${normalizeCell(fundsFollowedDisplay)}.`;
  if (normalizeCell(amountDisplay)) return `The transaction amount shown on this row was ${normalizeCell(amountDisplay)}.`;
  if (normalizeCell(token)) return `The transaction amount shown on this row was ${normalizeCell(token)}.`;
  return '';
}

export function buildCaseAddendumNarrativeParagraph(row = {}) {
  const parts = [];
  let first = `At TXN ID #${row.displayPathLabel || '(path)'}`;
  if (row.dateUtc) first += `, dated ${row.dateUtc}`;
  first += `, funds were followed to ${row.resolvedEntityName || '(entity)'}`;
  if (row.toDisplay && row.toDisplay !== 'Unknown Receiver') {
    first += ` at ${row.toDisplay}`;
    if (row.toAddress) first += ` (${row.toAddress})`;
  }
  first += '.';
  parts.push(first);
  if (row.fromDisplay && row.fromDisplay !== 'Unknown Sender') {
    let fromText = `The transaction moved from ${row.fromDisplay}`;
    if (row.fromAddress) fromText += ` (${row.fromAddress})`;
    parts.push(`${fromText}.`);
  }
  parts.push(`The transaction hash was ${row.transactionHash || '(hash)'}.`);
  const movement = buildCaseAddendumMovementText(row.amountDisplay, row.tokenSymbol, row.fundsFollowedDisplay);
  if (movement) parts.push(movement);
  if (row.notes) {
    let noteText = `Recorded notes for this path ending were: ${row.notes}`;
    if (!/[.!?]$/.test(noteText.trim())) noteText += '.';
    parts.push(noteText);
  }
  return parts.join(' ').trim();
}

export function buildCaseAddendumParagraphRow(candidate = {}, legendLookup = new Map()) {
  const normalizedOutputRow = normalizeCaseMatrix([candidate.outputRow || []], 1, 26)[0].slice(0, 26);
  const resolvedEntityName = normalizeCell(candidate.resolvedEntityName || normalizedOutputRow[10]);
  const pathLabel = normalizeCell(candidate.effectivePathLabel || normalizedOutputRow[0]);
  const transactionHash = normalizeCell(candidate.effectiveTransactionHash || normalizedOutputRow[1]);
  const fromAddress = normalizeCell(candidate.effectiveFromAddress || normalizedOutputRow[2]);
  const toAddress = normalizeCell(candidate.toAddress || normalizedOutputRow[3]);
  const dateUtc = normalizeCell(candidate.dateUtc || normalizedOutputRow[4]);
  const amount = normalizeCell(candidate.amount || normalizedOutputRow[5]);
  const tokenSymbol = normalizeCell(candidate.tokenSymbol || normalizedOutputRow[6]);
  const notes = normalizeCell(candidate.notes || normalizedOutputRow[9]);
  const fundsFollowed = normalizeCell(candidate.fundsFollowed || normalizedOutputRow[12]);
  const toLegendLabel = resolveCaseAddendumAddressLabel(legendLookup, toAddress);
  const fromLegendLabel = resolveCaseAddendumAddressLabel(legendLookup, fromAddress);
  const toAddressLabel = toLegendLabel || 'Likely deposit address';
  const addressType = buildCaseAddendumAddressType(toLegendLabel, notes);
  const network = determineCaseAddendumNetwork(toAddress, tokenSymbol);
  const amountDisplay = buildCaseAddendumAmountDisplay(amount, tokenSymbol);
  const fundsInfo = buildCaseAddendumFundsFollowedDisplay(amount, tokenSymbol, fundsFollowed);
  const appendLikelyMarker = /likely/i.test(notes) || /likely/i.test(addressType);
  const displayPathLabel = normalizeCaseAddendumPathLabelForDisplay(pathLabel, appendLikelyMarker);

  const row = {
    key: candidate.reviewKey || `${transactionHash}|${pathLabel}`,
    rowId: candidate.rowId || '',
    sourceRowNumber: Number(candidate.sourceRowNumber) || 0,
    pathLabel,
    transactionHash,
    resolvedEntityName,
    dateUtc,
    fromAddress,
    toAddress,
    fromDisplay: fromLegendLabel || 'Unknown Sender',
    toDisplay: toLegendLabel || 'Unknown Receiver',
    amount,
    tokenSymbol,
    fundsFollowed,
    notes,
    jurisdiction: lookupCaseAddendumJurisdiction(resolvedEntityName),
    toAddressLabel,
    addressType,
    network,
    amountDisplay,
    fundsFollowedDisplay: fundsInfo.display,
    fundsFollowedNote: fundsInfo.note,
    appendLikelyMarker,
    displayPathLabel,
    outputRow: normalizedOutputRow,
    searchText: ''
  };

  row.generatedStructuredBlock = buildCaseAddendumStructuredBlock(row);
  row.generatedStructuredHtmlBlock = buildCaseAddendumStructuredHtmlBlock(row);
  row.generatedParagraph = buildCaseAddendumNarrativeParagraph(row);
  row.searchText = [
    row.sourceRowNumber,
    row.pathLabel,
    row.displayPathLabel,
    row.transactionHash,
    row.resolvedEntityName,
    row.jurisdiction,
    row.fromAddress,
    row.toAddress,
    row.fromDisplay,
    row.toDisplay,
    row.amountDisplay,
    row.fundsFollowedDisplay,
    row.notes
  ].map(value => normalizeCell(value).toLowerCase()).join(' ');
  return row;
}

export function buildCaseAddendumAggregation(rows = []) {
  const countByHash = new Map();
  const sumsByHash = new Map();
  const idOrderByHash = new Map();
  const starByHashAndId = new Map();
  const firstIndexByHash = new Map();

  rows.forEach((row, index) => {
    const hash = normalizeCell(row.transactionHash);
    if (!hash) return;
    countByHash.set(hash, (countByHash.get(hash) || 0) + 1);
    if (!firstIndexByHash.has(hash)) firstIndexByHash.set(hash, index);

    const parsed = parseCaseAddendumFundsFollowedValue(row.fundsFollowed);
    if (parsed) {
      let tokenMap = sumsByHash.get(hash);
      if (!tokenMap) {
        tokenMap = new Map();
        sumsByHash.set(hash, tokenMap);
      }
      tokenMap.set(parsed.token, (tokenMap.get(parsed.token) || 0) + parsed.amount);
    }

    const baseId = normalizeCell(row.pathLabel || row.displayPathLabel).replace(/\*+$/g, '').trim();
    if (baseId) {
      if (!idOrderByHash.has(hash)) idOrderByHash.set(hash, []);
      if (!starByHashAndId.has(hash)) starByHashAndId.set(hash, new Map());
      const order = idOrderByHash.get(hash);
      const starMap = starByHashAndId.get(hash);
      if (!starMap.has(baseId)) order.push(baseId);
      starMap.set(baseId, !!starMap.get(baseId) || !!row.appendLikelyMarker || /\*$/.test(normalizeCell(row.displayPathLabel)));
    }
  });

  const duplicateGroups = [];
  countByHash.forEach((count, hash) => {
    if (count > 1) duplicateGroups.push({ hash, count, omittedRows: count - 1 });
  });
  duplicateGroups.sort((left, right) => right.count - left.count || left.hash.localeCompare(right.hash));

  const mergedIdsByHash = new Map();
  idOrderByHash.forEach((order, hash) => {
    const starMap = starByHashAndId.get(hash) || new Map();
    mergedIdsByHash.set(hash, order.map(id => `${id}${starMap.get(id) ? '*' : ''}`).join(','));
  });

  return {
    countByHash,
    sumsByHash,
    firstIndexByHash,
    mergedIdsByHash,
    duplicateGroups,
    duplicateGroupCount: duplicateGroups.length,
    omittedDuplicateRowCount: duplicateGroups.reduce((sum, group) => sum + group.omittedRows, 0)
  };
}

export function applyCaseAddendumAggregation(rows = [], aggregate = false) {
  if (!aggregate) return { rows: rows.slice(), aggregation: null };
  const aggregation = buildCaseAddendumAggregation(rows);
  const emitted = [];
  const seen = new Set();
  rows.forEach((row) => {
    const hash = normalizeCell(row.transactionHash);
    if (!hash) {
      emitted.push(row);
      return;
    }
    if (seen.has(hash)) return;
    seen.add(hash);
    const next = { ...row };
    const merged = aggregation.mergedIdsByHash.get(hash);
    if (merged) next.displayPathLabel = merged;
    const tokenMap = aggregation.sumsByHash.get(hash);
    const count = aggregation.countByHash.get(hash) || 0;
    if (count > 1 && tokenMap?.size) {
      const fundsInfo = buildCaseAddendumAggregatedFundsDisplay(tokenMap, next.amount, next.tokenSymbol);
      next.fundsFollowedDisplay = fundsInfo.display || next.fundsFollowedDisplay;
      next.fundsFollowedNote = fundsInfo.note || next.fundsFollowedNote;
    }
    next.generatedStructuredBlock = buildCaseAddendumStructuredBlock(next);
    next.generatedStructuredHtmlBlock = buildCaseAddendumStructuredHtmlBlock(next);
    next.generatedParagraph = buildCaseAddendumNarrativeParagraph(next);
    next.searchText = [next.searchText, next.displayPathLabel, next.fundsFollowedDisplay, next.fundsFollowedNote].join(' ').toLowerCase();
    emitted.push(next);
  });
  return { rows: emitted, aggregation };
}

export function buildCaseAddendumCondensedSummaryText(aggregation = null) {
  if (!aggregation || (aggregation.duplicateGroupCount || 0) <= 0) return '';
  const lines = [
    'Duplicate transactions were condensed.',
    `Because Aggregate Funds Followed is enabled, this output shows only the first entry for each duplicate transaction hash. ${aggregation.duplicateGroupCount} duplicate txn-hash group(s) were condensed and ${aggregation.omittedDuplicateRowCount} duplicate row(s) were omitted from the addendum output.`
  ];
  aggregation.duplicateGroups.slice(0, 20).forEach(group => lines.push(`- ${group.hash} (${group.count} rows -> 1 entry)`));
  if (aggregation.duplicateGroups.length > 20) lines.push(`- And ${aggregation.duplicateGroups.length - 20} more duplicate transaction hash group(s).`);
  return lines.join('\n');
}

export function buildCaseAddendumCondensedSummaryHtml(aggregation = null) {
  if (!aggregation || (aggregation.duplicateGroupCount || 0) <= 0) return '';
  const list = aggregation.duplicateGroups.slice(0, 20).map(group => `<li style="margin-bottom:4px;"><code>${escapeCaseAddendumHtml(group.hash)}</code> (${group.count} rows -&gt; 1 entry)</li>`).join('');
  const more = aggregation.duplicateGroups.length > 20
    ? `<div style="margin-top:6px;">And ${aggregation.duplicateGroups.length - 20} more duplicate transaction hash group(s) were condensed.</div>`
    : '';
  return `<div style="font-family: Arial; font-size: 14px; color: #222; border: 1px solid #ddd; padding: 12px; border-radius: 8px; margin-bottom: 14px;">
  <div style="font-size: 16px; font-weight: bold; margin-bottom: 6px;">Duplicate transactions were condensed</div>
  <div style="margin-bottom: 8px;">Because Aggregate Funds Followed is enabled, this output shows only the first entry for each duplicate transaction hash and summarizes Funds Followed values into the remaining entry.</div>
  <div style="margin-bottom: 8px;"><b>${aggregation.duplicateGroupCount}</b> duplicate txn-hash group(s) were condensed. <b>${aggregation.omittedDuplicateRowCount}</b> row(s) were omitted from the output.</div>
  <div style="font-weight: bold; margin-bottom: 4px;">Condensed hashes:</div>
  <ul style="margin: 0 0 0 18px; padding: 0;">${list}</ul>
  ${more}
</div>`;
}

export function buildCaseAddendumSections(rows = []) {
  const byEntity = new Map();
  rows.forEach((row) => {
    const entity = normalizeCell(row.resolvedEntityName) || '(entity)';
    if (!byEntity.has(entity)) byEntity.set(entity, {
      key: `addendum-section-${caseBuddyHashString(entity)}`,
      entityName: entity,
      jurisdiction: row.jurisdiction || lookupCaseAddendumJurisdiction(entity),
      rowCount: 0,
      rows: [],
      searchText: ''
    });
    const section = byEntity.get(entity);
    section.rows.push(row);
    section.rowCount += 1;
  });
  const sections = Array.from(byEntity.values());
  sections.forEach((section) => {
    section.rows.sort((left, right) => {
      const rowCompare = (Number(left.sourceRowNumber) || Number.MAX_SAFE_INTEGER) - (Number(right.sourceRowNumber) || Number.MAX_SAFE_INTEGER);
      if (rowCompare) return rowCompare;
      return normalizeCell(left.pathLabel).localeCompare(normalizeCell(right.pathLabel), undefined, { sensitivity: 'base', numeric: true });
    });
    section.pathLabelsText = section.rows.map(row => row.displayPathLabel || row.pathLabel).filter(Boolean).join(', ');
    section.previewText = section.rows.slice(0, 3).map(row => `${row.displayPathLabel || row.pathLabel} ${row.transactionHash}`).join(' · ');
    section.searchText = [section.entityName, section.jurisdiction, section.pathLabelsText, section.previewText, ...section.rows.map(row => row.searchText)].join(' ').toLowerCase();
  });
  sections.sort((left, right) => {
    const entityCompare = left.entityName.localeCompare(right.entityName, undefined, { sensitivity: 'base' });
    if (entityCompare) return entityCompare;
    const leftRow = left.rows[0]?.sourceRowNumber || Number.MAX_SAFE_INTEGER;
    const rightRow = right.rows[0]?.sourceRowNumber || Number.MAX_SAFE_INTEGER;
    return leftRow - rightRow;
  });
  return sections;
}

export function buildCaseAddendumStructuredText(state = {}) {
  if (!state.canBuild) return state.unavailableMessage || 'The addendum cannot be built yet.';
  if (!state.sections?.length) return 'No reviewed exchange path-ending rows are currently included for the addendum.';
  const chunks = [];
  if (state.condensedSummaryText) chunks.push(state.condensedSummaryText);
  state.sections.forEach((section) => {
    chunks.push([
      `Exchange: ${section.entityName || '(exchange)'}`,
      `Jurisdiction: ${section.jurisdiction || 'Unknown'}`,
      ...section.rows.map(row => `
${row.generatedStructuredBlock}`)
    ].join('\n'));
  });
  return chunks.join('\n\n').trim();
}

export function buildCaseAddendumNarrativeText(state = {}) {
  if (!state.canBuild) return state.unavailableMessage || 'The addendum cannot be built yet.';
  if (!state.sections?.length) return 'No reviewed exchange path-ending rows are currently included for the addendum.';
  const lines = ['Exchange Path Endings Addendum', '', 'This addendum documents the reviewed exchange path-ending rows identified in the case, grouped by the resolved recipient entity.'];
  if (state.condensedSummaryText) lines.push('', state.condensedSummaryText);
  state.sections.forEach((section) => {
    lines.push('', section.entityName, '-'.repeat(Math.max(8, normalizeCell(section.entityName).length)));
    section.rows.forEach((row, index) => {
      if (index > 0) lines.push('');
      lines.push(row.generatedParagraph);
    });
  });
  return lines.join('\n').trim();
}

export function buildCaseAddendumStructuredHtmlFragment(state = {}) {
  if (!state.canBuild || !state.sections?.length) return '';
  const chunks = [];
  if (state.condensedSummaryHtml) chunks.push(state.condensedSummaryHtml);
  state.sections.forEach((section) => {
    const heading = '<div style="font-family: Arial; color: black;">' +
      `<div style="font-size: 20px; font-weight: bold;">Exchange: ${escapeCaseAddendumHtml(section.entityName || '(exchange)')}</div>` +
      `<div style="font-size: 14.5px; margin-top: 2px;">Jurisdiction: ${escapeCaseAddendumHtml(section.jurisdiction || 'Unknown')}</div>` +
      '</div>';
    const body = section.rows.map(row => buildCaseAddendumStructuredHtmlBlock(row)).join('<br><br>');
    chunks.push(`${heading}<br><br>${body}`);
  });
  return chunks.join('<div style="height: 18px;"></div>');
}

export function buildCaseAddendumNarrativeHtmlFragment(state = {}) {
  if (!state.canBuild || !state.sections?.length) return '';
  const chunks = [
    '<div style="font-family: Arial; font-size: 20px; color: black; font-weight: bold;">Exchange Path Endings Addendum</div>',
    '<div style="height: 10px;"></div>',
    '<div style="font-family: Arial; font-size: 14.5px; color: black;">This addendum documents the reviewed exchange path-ending rows identified in the case, grouped by the resolved recipient entity.</div>'
  ];
  if (state.condensedSummaryHtml) chunks.push('<div style="height: 10px;"></div>', state.condensedSummaryHtml);
  state.sections.forEach((section) => {
    chunks.push('<div style="height: 14px;"></div>');
    chunks.push(`<div style="font-family: Arial; font-size: 18px; color: black; font-weight: bold;">${escapeCaseAddendumHtml(section.entityName)}</div>`);
    chunks.push('<hr>');
    section.rows.forEach((row) => {
      chunks.push(`<div style="font-family: Arial; font-size: 14.5px; color: black; margin-top: 8px;">${escapeCaseAddendumHtml(row.generatedParagraph).replace(/\n/g, '<br>')}</div>`);
    });
  });
  return chunks.join('');
}

export function wrapCaseAddendumHtmlDocument(fragment = '') {
  return `<html><head><meta charset="utf-8"></head><body>${normalizeCell(fragment)}</body></html>`;
}

export function buildCaseAddendumUnavailableMessage(prep55Current = false, prep48Current = false) {
  if (!prep55Current) return 'The addendum cannot be built until the Tool55-style reviewed candidate set is current.';
  if (!prep48Current) return 'The addendum cannot be built until the Tool48-style resolved entity choices are current.';
  return 'The addendum cannot be built yet.';
}

export function buildCaseAddendumReviewBasisHash(state = {}) {
  return `case-addendum-${caseBuddyHashString(JSON.stringify({
    rendererVersion: state.rendererVersion,
    aggregateDuplicateHashes: !!state.aggregateDuplicateHashes,
    prep55BasisHash: state.prep55BasisHash,
    prep48BasisHash: state.prep48BasisHash,
    resolvedRowCount: state.resolvedRowCount,
    sectionCount: state.sectionCount,
    structuredText: state.generatedAddendumText,
    narrativeText: state.generatedNarrativeAddendumText,
    rows: (state.rows || []).map(row => [row.rowId, row.sourceRowNumber, row.displayPathLabel, row.transactionHash, row.resolvedEntityName, row.generatedStructuredBlock, row.generatedParagraph])
  }))}`;
}

export function buildCaseAddendumState(recipientEntityState = {}, workflowState = {}, options = {}) {
  const prep55ReviewIsCurrent = !!recipientEntityState.pathEndingReviewIsCurrent;
  const prep48ReviewIsCurrent = !!recipientEntityState.reviewIsCurrent;
  const canBuild = prep55ReviewIsCurrent && prep48ReviewIsCurrent;
  const aggregateDuplicateHashes = !!options.aggregateDuplicateHashes;
  const caseDataRevision = Number(options.caseDataRevision) || 0;
  const rendererVersion = CASE_ADDENDUM_RENDERER_VERSION;
  const legendEntries = Array.isArray(options.addressLegendEntries)
    ? options.addressLegendEntries
    : Array.isArray(options.addressLegendAnalysis?.storedEntries) && options.addressLegendAnalysis.reviewIsCurrent
      ? options.addressLegendAnalysis.storedEntries
      : Array.isArray(options.addressLegendAnalysis?.entries)
        ? options.addressLegendAnalysis.entries
        : [];
  const legendLookup = buildCaseAddendumLegendLookup(legendEntries);
  const sourceCandidates = (Array.isArray(recipientEntityState.candidates) ? recipientEntityState.candidates : [])
    .filter(candidate => !!candidate?.resolvedEntityName);
  const unavailableMessage = buildCaseAddendumUnavailableMessage(prep55ReviewIsCurrent, prep48ReviewIsCurrent);
  let rows = [];
  let aggregation = null;
  if (canBuild) {
    const builtRows = sourceCandidates.map(candidate => buildCaseAddendumParagraphRow(candidate, legendLookup));
    const aggregateResult = applyCaseAddendumAggregation(builtRows, aggregateDuplicateHashes);
    rows = aggregateResult.rows;
    aggregation = aggregateResult.aggregation;
  }
  const sections = canBuild ? buildCaseAddendumSections(rows) : [];
  const state = {
    reviewId: CASE_ADDENDUM_BUILD_REVIEW_ID,
    rendererVersion,
    canBuild,
    prep55ReviewIsCurrent,
    prep48ReviewIsCurrent,
    prep55BasisHash: recipientEntityState.pathEndingBasisHash || '',
    prep48BasisHash: recipientEntityState.currentReviewBasisHash || '',
    aggregateDuplicateHashes,
    unavailableMessage,
    sourceCandidateCount: sourceCandidates.length,
    resolvedRowCount: rows.length,
    rawResolvedRowCount: sourceCandidates.length,
    sectionCount: sections.length,
    sections,
    rows,
    aggregation,
    duplicateGroupCount: aggregation?.duplicateGroupCount || 0,
    condensedOmittedRowCount: aggregation?.omittedDuplicateRowCount || 0,
    condensedSummaryText: buildCaseAddendumCondensedSummaryText(aggregation),
    condensedSummaryHtml: buildCaseAddendumCondensedSummaryHtml(aggregation),
    legendEntryCount: legendEntries.length,
    caseDataRevision,
    activePreviewVariantId: options.activePreviewVariantId || CASE_ADDENDUM_VARIANT_STRUCTURED,
    generatedAddendumText: '',
    generatedNarrativeAddendumText: '',
    structuredHtmlFragment: '',
    narrativeHtmlFragment: '',
    structuredHtmlDocument: '',
    narrativeHtmlDocument: '',
    generatedAuditTsv: ''
  };
  state.generatedAddendumText = buildCaseAddendumStructuredText(state);
  state.generatedNarrativeAddendumText = buildCaseAddendumNarrativeText(state);
  state.structuredHtmlFragment = buildCaseAddendumStructuredHtmlFragment(state);
  state.narrativeHtmlFragment = buildCaseAddendumNarrativeHtmlFragment(state);
  state.structuredHtmlDocument = wrapCaseAddendumHtmlDocument(state.structuredHtmlFragment);
  state.narrativeHtmlDocument = wrapCaseAddendumHtmlDocument(state.narrativeHtmlFragment);
  state.currentReviewBasisHash = buildCaseAddendumReviewBasisHash(state);
  const review = workflowState?.addendumBuildReview || workflowState?.caseAddendumReview || null;
  state.confirmedReviewBasisHash = normalizeCell(review?.basisHash);
  state.confirmedAtUtc = normalizeCell(review?.confirmedAtUtc || review?.reviewedAtUtc);
  state.reviewedAtUtc = normalizeCell(review?.reviewedAtUtc || review?.confirmedAtUtc);
  state.reviewedRevision = Number(review?.revision);
  state.reviewIsCurrent = canBuild && !!state.reviewedAtUtc && state.confirmedReviewBasisHash === state.currentReviewBasisHash && Number(review?.revision) === caseDataRevision;
  state.generatedAuditTsv = '';
  state.summary = !canBuild
    ? unavailableMessage
    : (state.resolvedRowCount || 0) === 0
      ? state.reviewIsCurrent
        ? 'No exchange path-ending rows were identified for the addendum. The no-addendum review is current.'
        : 'No exchange path-ending rows are currently included for the addendum. Mark reviewed to record that no addendum is needed for this case revision.'
      : state.reviewIsCurrent
        ? `Addendum is current and reviewed for ${state.resolvedRowCount} resolved row${state.resolvedRowCount === 1 ? '' : 's'} across ${state.sectionCount} recipient entit${state.sectionCount === 1 ? 'y' : 'ies'}.`
        : `Addendum is ready to review: ${state.resolvedRowCount} resolved row${state.resolvedRowCount === 1 ? '' : 's'} across ${state.sectionCount} recipient entit${state.sectionCount === 1 ? 'y' : 'ies'}. Review is not current.`;
  state.statsSummary = [
    `Reviewed Tool55 current: ${prep55ReviewIsCurrent ? 'Yes' : 'No'}`,
    `Reviewed Tool48 current: ${prep48ReviewIsCurrent ? 'Yes' : 'No'}`,
    `Resolved path-ending rows: ${state.resolvedRowCount}`,
    `Entity sections: ${state.sectionCount}`,
    `Address legend entries: ${state.legendEntryCount}`,
    `Aggregate duplicate hashes: ${aggregateDuplicateHashes ? 'Yes' : 'No'}`,
    `Renderer version: ${rendererVersion}`
  ].join('\n');
  return state;
}

export function getCaseAddendumVariantPayload(state = {}, variantId = CASE_ADDENDUM_VARIANT_STRUCTURED) {
  const narrative = variantId === CASE_ADDENDUM_VARIANT_NARRATIVE;
  return {
    variantId: narrative ? CASE_ADDENDUM_VARIANT_NARRATIVE : CASE_ADDENDUM_VARIANT_STRUCTURED,
    deliverableId: narrative ? CASE_ADDENDUM_DELIVERABLE_ID_NARRATIVE : CASE_ADDENDUM_DELIVERABLE_ID_STRUCTURED,
    displayName: narrative ? 'Addendum narrative' : 'Tool18E structured addendum',
    filenameStem: narrative ? 'addendum_narrative' : 'addendum',
    plainText: narrative ? (state.generatedNarrativeAddendumText || '') : (state.generatedAddendumText || ''),
    htmlFragment: narrative ? (state.narrativeHtmlFragment || '') : (state.structuredHtmlFragment || ''),
    htmlDocument: narrative ? (state.narrativeHtmlDocument || '') : (state.structuredHtmlDocument || '')
  };
}

export function buildCaseAddendumAuditRows(state = {}, actionLabel = 'Addendum review') {
  return [
    ['Case Buddy Addendum Audit', '', '', '', '', '', '', '', '', 'Audit only. Built from reviewed Tool55/Tool48 addendum state; Main Data is not modified.'],
    ['Action', actionLabel, 'Basis', state.currentReviewBasisHash || '', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No'],
    ['Tool55 Current', state.prep55ReviewIsCurrent ? 'Yes' : 'No', 'Tool48 Current', state.prep48ReviewIsCurrent ? 'Yes' : 'No', 'Renderer', state.rendererVersion || ''],
    ['Resolved Rows', state.resolvedRowCount || 0, 'Raw Resolved Rows', state.rawResolvedRowCount || 0, 'Entity Sections', state.sectionCount || 0, 'Legend Entries', state.legendEntryCount || 0],
    ['Aggregate Duplicate Hashes', state.aggregateDuplicateHashes ? 'Yes' : 'No', 'Duplicate Groups Condensed', state.duplicateGroupCount || 0, 'Condensed Rows Omitted', state.condensedOmittedRowCount || 0],
    [],
    ['Entity Sections'],
    ['Entity', 'Jurisdiction', 'Row Count', 'Path Labels'],
    ...(state.sections || []).map(section => [section.entityName, section.jurisdiction, section.rowCount, section.pathLabelsText]),
    [],
    ['Generated Rows'],
    ['Entity', 'Source Row', 'Path Label', 'Display ID', 'Transaction Hash', 'From Address', 'To Address', 'To Address Label', 'Address Type', 'Network', 'Amount', 'Funds Followed', 'Funds Followed Note', 'Column J Notes', 'Generated Paragraph'],
    ...(state.rows || []).map(row => [
      row.resolvedEntityName,
      row.sourceRowNumber || '',
      row.pathLabel,
      row.displayPathLabel,
      row.transactionHash,
      row.fromAddress,
      row.toAddress,
      row.toAddressLabel,
      row.addressType,
      row.network,
      row.amountDisplay,
      row.fundsFollowedDisplay,
      row.fundsFollowedNote,
      row.notes,
      row.generatedParagraph
    ])
  ].filter(row => Array.isArray(row) ? row.length === 0 || rowHasMeaningfulData(row) : true);
}

export function buildCaseAddendumPreviewRows(state = {}, variantId = CASE_ADDENDUM_VARIANT_STRUCTURED) {
  const payload = getCaseAddendumVariantPayload(state, variantId);
  return [
    ['Case Buddy Addendum Preview', '', '', '', '', '', '', '', '', 'Preview/audit only. Addendum output is generated from reviewed addendum recipient rows; Main Data is not modified.'],
    ['Variant', payload.displayName, 'Basis', state.currentReviewBasisHash || '', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No'],
    ['Resolved Rows', state.resolvedRowCount || 0, 'Sections', state.sectionCount || 0, 'Aggregate Duplicate Hashes', state.aggregateDuplicateHashes ? 'Yes' : 'No'],
    [],
    ['Plain Text Output'],
    [payload.plainText || ''],
    [],
    ['Sections'],
    ['Entity', 'Jurisdiction', 'Row Count', 'Path Labels'],
    ...(state.sections || []).map(section => [section.entityName, section.jurisdiction, section.rowCount, section.pathLabelsText])
  ];
}

export function buildCaseAddendumDownloadText(state = {}, variantId = CASE_ADDENDUM_VARIANT_STRUCTURED) {
  return getCaseAddendumVariantPayload(state, variantId).plainText || '';
}
